import { DecoratorNode, $applyNodeReplacement, $getNodeByKey } from 'lexical';
import React, { useRef, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useLexicalNodeSelection } from '@lexical/react/useLexicalNodeSelection';

const MIN_IMAGE_WIDTH = 60;

function ImageComponent({ nodeKey, src, alt, width, height, style }) {
  const [editor] = useLexicalComposerContext();
  const [isSelected, setSelected, clearSelection] = useLexicalNodeSelection(nodeKey);
  const imageRef = useRef(null);
  const resizeStartRef = useRef(null);

  useEffect(() => {
    if (imageRef.current) imageRef.current.setAttribute('style', style || '');
  }, [style]);

  const onImageClick = useCallback(
    event => {
      event.stopPropagation();
      clearSelection();
      setSelected(true);
    },
    [clearSelection, setSelected]
  );

  const onResizeHandlePointerDown = useCallback(
    event => {
      event.preventDefault();
      event.stopPropagation();
      const imageElement = imageRef.current;
      if (!imageElement) return;

      resizeStartRef.current = {
        startX: event.clientX,
        startWidth: imageElement.getBoundingClientRect().width
      };

      const onPointerMove = moveEvent => {
        const { startX, startWidth } = resizeStartRef.current;
        const nextWidth = Math.max(MIN_IMAGE_WIDTH, startWidth + (moveEvent.clientX - startX));
        imageElement.style.width = `${nextWidth}px`;
        imageElement.style.height = 'auto';
      };

      const onPointerUp = () => {
        window.removeEventListener('pointermove', onPointerMove);
        window.removeEventListener('pointerup', onPointerUp);
        const finalWidth = Math.round(imageElement.getBoundingClientRect().width);
        editor.update(() => {
          const node = $getNodeByKey(nodeKey);
          if ($isImageNode(node)) {
            node.setWidthAndHeight(finalWidth, 'auto');
          }
        });
      };

      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', onPointerUp);
    },
    [editor, nodeKey]
  );

  return (
    <span
      className={`editor-image-wrapper${isSelected ? ' editor-image-wrapper-selected' : ''}`}
      onClick={onImageClick}
    >
      <img ref={imageRef} src={src} alt={alt} width={width} height={height} draggable={false} />
      {isSelected && (
        <span
          className="editor-image-resize-handle"
          onPointerDown={onResizeHandlePointerDown}
        />
      )}
    </span>
  );
}

ImageComponent.propTypes = {
  nodeKey: PropTypes.string.isRequired,
  src: PropTypes.string,
  alt: PropTypes.string,
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  style: PropTypes.string
};

export class ImageNode extends DecoratorNode {
  static getType() {
    return 'image';
  }

  static clone(node) {
    return new ImageNode(node.__src, node.__alt, node.__width, node.__height, node.__style, node.__key);
  }

  static importJSON(serializedNode) {
    const { src, alt, width, height, style } = serializedNode;
    return $createImageNode({ src, alt, width, height, style });
  }

  exportJSON() {
    return {
      type: 'image',
      version: 1,
      src: this.__src,
      alt: this.__alt,
      width: this.__width,
      height: this.__height,
      style: this.__style
    };
  }

  static importDOM() {
    return {
      img: () => ({
        conversion: domNode => ({
          node: $createImageNode({
            src: domNode.getAttribute('src'),
            alt: domNode.getAttribute('alt') || '',
            width: domNode.getAttribute('width') || 'auto',
            height: domNode.getAttribute('height') || 'auto',
            style: domNode.getAttribute('style') || ''
          })
        }),
        priority: 1
      })
    };
  }

  exportDOM() {
    const element = document.createElement('img');
    element.setAttribute('src', this.__src);
    element.setAttribute('alt', this.__alt || '');
    if (this.__width) element.setAttribute('width', this.__width);
    if (this.__height) element.setAttribute('height', this.__height);
    if (this.__style) element.setAttribute('style', this.__style);
    return { element };
  }

  constructor(src, alt, width, height, style, key) {
    super(key);
    this.__src = src;
    this.__alt = alt || '';
    this.__width = width || 'auto';
    this.__height = height || 'auto';
    this.__style = style || 'max-width: 100%;';
  }

  setWidthAndHeight(width, height) {
    const writable = this.getWritable();
    writable.__width = width;
    writable.__height = height;
  }

  createDOM() {
    return document.createElement('span');
  }

  updateDOM() {
    return false;
  }

  decorate() {
    return (
      <ImageComponent
        nodeKey={this.getKey()}
        src={this.__src}
        alt={this.__alt}
        width={this.__width}
        height={this.__height}
        style={this.__style}
      />
    );
  }
}

export function $createImageNode({ src, alt = '', width = 'auto', height = 'auto', style = 'max-width: 100%;' }) {
  return $applyNodeReplacement(new ImageNode(src, alt, width, height, style));
}

export function $isImageNode(node) {
  return node instanceof ImageNode;
}
